/* global Office window */
import * as React from "react";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { getTemplateDefs } from "../helpers/grid";
import { reset } from "../state/selectionSlice";
import BorderPinHandler from "./dragAndDrop/BorderPinHandler";
import HorizontalContextMenuContent from "./contextmenus/HorizontalContextMenuContent/HorizontalContextMenuContent";
import { Menu, MenuTrigger, MenuPopover } from "@fluentui/react-components";

const Borders = () => {
  const dispatch = useDispatch();
  const uiScale = useSelector((state) => state.preferences.uiScale);
  const borders = useSelector((state) => state.gridd.present.borders);
  const cols = useSelector((state) => state.gridd.present.structure.columns);
  const rows = useSelector((state) => state.gridd.present.structure.rows);
  const merged = useSelector((state) => state.gridd.present.structure.merged);
  const selection = useSelector((state) => state.selection);
  const spacerStyles = useSelector((state) => state.gridd.present.spacerStyles);
  const cellStyles = useSelector((state) => state.gridd.present.cellStyles);
  const cells = useSelector((state) => state.gridd.present.structure.cells);
  const styleSheetDialogOpen = useSelector((state) => state.app.styleSheetDialogOpen);

  const [containerHeight, setContainerHeight] = useState(window.innerHeight);

  useEffect(() => {
    const updateHeight = () => setContainerHeight(window.innerHeight);
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  try {
    Office?.context?.document?.addHandlerAsync(Office.EventType.DocumentSelectionChanged, onSelectionChanged);
  } catch (error) {
    // no reaction
  }

  function onSelectionChanged() {
    dispatch(reset());
  }

  const hasExplicitSpacerColor = (isVertical, rowIndex, colIndex) => {
    const spacerColor = isVertical
      ? spacerStyles?.columnColors
        ? spacerStyles?.columnColors[colIndex]
        : false
      : spacerStyles?.rowColors
        ? spacerStyles?.rowColors[rowIndex]
        : false;

    if (spacerColor) {
      return true;
    }

    const cellId = isVertical ? cells[rowIndex]?.[colIndex]?.id : cells[rowIndex]?.[colIndex]?.id;
    const cellBackground = cellStyles[cellId]?.background;
    if (cellBackground) {
      return true;
    }

    return false;
  };

  // Pre-compute border lookups keyed by cell position so each cell does O(1)
  // lookups instead of scanning every border (previously borders.find() ran
  // 4x per cell). A border spans a range of cells, so it is expanded into the
  // positions it covers; the first border in array order wins, matching the
  // previous .find() semantics.
  const { verticalBorderAt, horizontalBorderAt } = React.useMemo(() => {
    const vMap = {};
    const hMap = {};
    borders.forEach((b) => {
      const to = b.to ?? Number.MAX_VALUE;
      if (b.isVertical) {
        const end = Math.min(to, rows.length);
        for (let i = b.from; i < end; i++) {
          const key = `${b.index},${i}`;
          if (vMap[key] === undefined) vMap[key] = b;
        }
      } else {
        const end = Math.min(to, cols.length);
        for (let j = b.from; j < end; j++) {
          const key = `${b.index},${j}`;
          if (hMap[key] === undefined) hMap[key] = b;
        }
      }
    });
    return { verticalBorderAt: vMap, horizontalBorderAt: hMap };
  }, [borders, rows.length, cols.length]);

  // Pre-compute which border positions fall inside a merge (previously
  // merged.some() ran up to 4x per cell). Keys mirror the old
  // isBorderInsideMerge() arguments: vertical -> `${rowIdx},${borderIndex}`,
  // horizontal -> `${colIdx},${borderIndex}`.
  const { mergedVerticalBorders, mergedHorizontalBorders } = React.useMemo(() => {
    const vSet = new Set();
    const hSet = new Set();
    merged.forEach((m) => {
      if (m.dir === "right") {
        for (let bi = m.cell.y + 1; bi <= m.cell.y + m.length; bi++) {
          vSet.add(`${m.cell.x},${bi}`);
        }
      } else if (m.dir === "down") {
        for (let bi = m.cell.x + 1; bi <= m.cell.x + m.length; bi++) {
          hSet.add(`${m.cell.y},${bi}`);
        }
      }
    });
    return { mergedVerticalBorders: vSet, mergedHorizontalBorders: hSet };
  }, [merged]);

  return borders && borders.length > 0 ? (
    <div
      id="border_grid"
      style={{
        display: "grid",
        width: "calc(100% + 1px)",
        height: `calc(100% + 1px)`,
        gridTemplateColumns: "28px 1fr 28px",
        gridTemplateRows: "28px 1fr 28px",
        zIndex: 6,
        position: "absolute",
        pointerEvents: "none",
      }}
    >
      <div
        id="borders_topleft"
        style={{
          height: "28px",
          width: "28px",
        }}
      />
      <div id="borders_top" />
      <div id="borders_topright" />

      <div id="borders_left" />
      <div
        id="borders_cells"
        style={{
          display: "grid",
          width: "100%",
          height: `${containerHeight - 2 * 28}px`,
          gridTemplateColumns: getTemplateDefs(cols),
          gridTemplateRows: getTemplateDefs(rows),
        }}
      >
        {rows?.map((row, i) =>
          cols?.map((col, j) => {
            const rowSpacer = row[0] === "s";
            const colSpacer = col[0] === "s";

            const leftBorder = verticalBorderAt[`${j},${i}`];

            const leftSelected = selection.borders.find(
              (b) => b.isVertical && b.index === j && b.id === leftBorder?.id
            );

            const topBorder = horizontalBorderAt[`${i},${j}`];

            const topSelected = selection.borders.find((b) => !b.isVertical && b.index === i && b.id === topBorder?.id);

            const rightBorder = j === cols.length - 1 ? verticalBorderAt[`${j + 1},${i}`] : undefined;

            const rightSelected = selection.borders.find(
              (b) => j === cols.length - 1 && b.isVertical && b.index === j + 1 && b.id === rightBorder?.id
            );

            const bottomBorder = i === rows.length - 1 ? horizontalBorderAt[`${i + 1},${j}`] : undefined;

            const bottomSelected = selection.borders.find(
              (b) => i === rows.length - 1 && !b.isVertical && b.index === i + 1 && b.id === bottomBorder?.id
            );

            return (
              <div
                key={uuidv4()}
                style={{
                  //boxSizing: "border-box",
                  borderLeftWidth: leftBorder?.weight,
                  borderLeftColor: leftBorder?.color,
                  borderLeftStyle:
                    (rowSpacer && hasExplicitSpacerColor(false, i, j)) || mergedVerticalBorders.has(`${i},${j}`)
                      ? "none"
                      : leftBorder?.style,
                  borderTopWidth: topBorder?.weight,
                  borderTopColor: topBorder?.color,
                  borderTopStyle:
                    (colSpacer && hasExplicitSpacerColor(true, i, j)) || mergedHorizontalBorders.has(`${j},${i}`)
                      ? "none"
                      : topBorder?.style,
                  borderRightWidth: rightBorder?.weight,
                  borderRightColor: rightBorder?.color,
                  borderRightStyle:
                    (rowSpacer && hasExplicitSpacerColor(false, i, j)) || mergedVerticalBorders.has(`${i},${j + 1}`)
                      ? "none"
                      : rightBorder?.style,
                  borderBottomWidth: bottomBorder?.weight,
                  borderBottomColor: bottomBorder?.color,
                  borderBottomStyle:
                    (colSpacer && hasExplicitSpacerColor(true, i, j)) || mergedHorizontalBorders.has(`${j},${i + 1}`)
                      ? "none"
                      : bottomBorder?.style,
                }}
              >
                {i === leftBorder?.from && leftSelected ? (
                  <BorderPinHandler
                    border={leftBorder}
                    left={leftSelected !== null}
                    cols={cols}
                    rows={rows}
                    from={true}
                  />
                ) : (
                  <></>
                )}

                {(leftSelected && i === leftBorder?.from) || (rightSelected && i === rightBorder?.from) ? (
                  <div
                    id="context_menu"
                    style={{
                      zIndex: 1020,
                      position: "absolute",
                      padding: 2,
                      pointerEvents: "all",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      background: "white",
                      top: 48,
                      transform: `scale(${uiScale ?? 1})`,
                    }}
                  >
                    <HorizontalContextMenuContent />
                  </div>
                ) : (
                  <></>
                )}

                {i === rightBorder?.from && rightSelected ? (
                  <BorderPinHandler border={rightBorder} left={false} cols={cols} rows={rows} from={true} />
                ) : (
                  <></>
                )}

                {(i === leftBorder?.to - 1 || (leftBorder?.to === null && i === rows.length - 1)) && leftSelected ? (
                  <BorderPinHandler
                    border={leftBorder}
                    left={leftSelected !== null}
                    cols={cols}
                    rows={rows}
                    i={i}
                    to={true}
                    direction={"down"}
                  />
                ) : (
                  <></>
                )}

                {(i === rightBorder?.to - 1 || (rightBorder?.to === null && i === rows.length - 1)) && rightSelected ? (
                  <BorderPinHandler
                    border={rightBorder}
                    left={false}
                    cols={cols}
                    rows={rows}
                    i={i}
                    to={true}
                    direction={"down"}
                  />
                ) : (
                  <></>
                )}

                {j === topBorder?.from && topSelected ? (
                  <BorderPinHandler
                    border={topBorder}
                    top={true}
                    cols={cols}
                    rows={rows}
                    from={true}
                    direction={"left"}
                  />
                ) : (
                  <></>
                )}

                {j === bottomBorder?.from && bottomSelected ? (
                  <BorderPinHandler
                    border={bottomBorder}
                    top={false}
                    cols={cols}
                    rows={rows}
                    from={true}
                    direction={"left"}
                  />
                ) : (
                  <></>
                )}

                {((topSelected && j === topBorder?.from) || (bottomSelected && j === bottomBorder?.from)) &&
                !(leftSelected || rightSelected || styleSheetDialogOpen) ? (
                  <div
                    id="context_menu"
                    style={{
                      zIndex: 11,
                      padding: 2,
                      pointerEvents: "all",
                    }}
                  >
                    <Menu open={true}>
                      <MenuTrigger>
                        <div style={{ position: "relative", left: 24 }}></div>
                      </MenuTrigger>

                      <MenuPopover
                        style={{
                          maxWidth: "none",
                          boxShadow: "none",
                          background: "transparent",
                          pointerEvents: "none",
                        }}
                      >
                        <div style={{ transform: `scale(${uiScale ?? 1})` }}>
                          <HorizontalContextMenuContent />
                        </div>
                      </MenuPopover>
                    </Menu>
                  </div>
                ) : (
                  <></>
                )}

                {(j === topBorder?.to - 1 || (topBorder?.to === null && j === cols.length - 1)) && topSelected ? (
                  <BorderPinHandler
                    border={topBorder}
                    top={true}
                    cols={cols}
                    rows={rows}
                    to={true}
                    direction={"right"}
                  />
                ) : (
                  <></>
                )}

                {(j === bottomBorder?.to - 1 || (bottomBorder?.to === null && j === cols.length - 1)) &&
                bottomSelected ? (
                  <BorderPinHandler
                    border={bottomBorder}
                    top={false}
                    cols={cols}
                    rows={rows}
                    to={true}
                    direction={"right"}
                  />
                ) : (
                  <></>
                )}
              </div>
            );
          })
        )}
      </div>

      <div id="borders_right" />

      <div id="borders_bottomleft" />

      <div id="borders_bottom" />

      <div id="borders_bottomright" />
    </div>
  ) : (
    <></>
  );
};

export default Borders;
